import {useEffect, useState} from "react"
import {message} from "antd"

/**
 * @typedef {object} Response
 * @property {number} status
 * @property {string} detail
 * @property {T} data
 * @template T
 */

/**
 * @typedef {object} FetchOptions
 * @property {boolean} immediate - 是否立即请求
 */

/**
 * @typedef {object} FetchType
 * @template T
 * @property {boolean} isLoading
 * @property {T} data
 * @property {null|string} error
 * @property {function(): Promise<Response<T>>} reFetch
 */

const fetchOptions={
    immediate: true
}

/**
 * @template T
 * @param fn {function(): Promise<Response<T>>}
 * @param options {FetchOptions?}
 * @return {FetchType<T>}
 */
export function useFetch(fn, options) {
    const [data, setData] = useState(null)
    const [error, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(true)

    options = {
        ...fetchOptions,
        ...options
    }

    const fetchData = async () => {
        setIsLoading(true)
        try {
            const res = await fn()
            if (res.status !== 1) {
                throw new Error(res.detail)
            }
            setData(res.data)
        } catch (error) {
            setError(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (options.immediate) {
            fetchData()
        }
    }, [])

    const reFetch = async () => fetchData()

    return {data, error, isLoading, reFetch}
}


/**
 * @typedef {object} PostOptions
 * @property {boolean} immediate - 是否立即请求
 * @property {string} errorMessagePrefixes - 报错信息前缀
 * @property {string} successMessage - 报错信息后缀
 * @Property {Function} callback - 请求成功后的回调函数
 */

/**
 * @typedef {object} PostType
 * @property {boolean} isLoading
 * @property {any} data
 * @property {function(): Promise<Response>} updateData
 */

const postOptions = {
    immediate: false,
    errorMessagePrefixes: '操作失败',
    successMessage: '操作成功',
    callback: () => {
    }
}


/**
 *
 * @param fn {function(): Promise<Response>}
 * @param options {PostOptions?}
 * @return {PostType}
 */
export function usePost(fn, options={}) {
    const [data, setData] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    options = {
        ...postOptions,
        ...options
    }

    const updateData = async () => {
        setIsLoading(true)
        try {
            const res = await fn()
            if (res.status !== 1) {
                throw new Error(res.detail)
            }
            setData(res.data)
            message.success(options.successMessage)
        } catch (error) {
            message.error(error.message)
        } finally {
            setIsLoading(false)
            options.callback()
        }
    }

    return {data, isLoading, updateData}

}


// WEBPACK FOOTER //
// ./src/util/useFetch.js